import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import {
  // Action Icons
  Add, Edit, Delete, Save, Close, Done, Cancel, Refresh, Settings, Share, Star, Download, Upload, Print, Send,
  // Communication Icons
  Phone, Email, Message, Chat, VideoCall, Call, Contacts,
  // Content Icons
  Archive, Create, Flag, Link, Mail, Reply, Sort, Undo,
  // Device Icons
  Bluetooth, Wifi, Battery20, Battery50, Battery80, BatteryFull,
  // Food & Drink Icons
  Restaurant, LocalBar, LocalCafe, LocalDining, LocalPizza, Cake, LocalGroceryStore, Kitchen, RoomService,
  // Healthcare Icons
  LocalHospital, MedicalServices, LocalPharmacy, Healing,
  // Emergency Icons
  LocalFireDepartment, ReportProblem, Announcement, PriorityHigh, NewReleases, NotificationImportant,
  // Home & Living Icons
  Home, House, Bed, Microwave,
  // Maps & Places Icons
  LocationOn, Directions, DirectionsCar, DirectionsBike, Flight, LocalShipping, LocalTaxi, Map, Place,
  // Navigation Icons
  ArrowBack, ArrowForward, ArrowUpward, ArrowDownward, ChevronLeft, ChevronRight, ExpandMore, ExpandLess, Menu,
  // People Icons
  Person, Group, PersonAdd, Face, People,
  // Technology Icons
  Computer, Laptop, Smartphone, Tablet, Camera, Monitor, Speaker,
  // Transportation Icons
  LocalShipping as Shipping, Train, Subway, DirectionsBus, TwoWheeler,
  // Work & Business Icons
  Work, Business, Assignment, CalendarToday, Event, Folder, Description, Dashboard,
  // Services Icons
  Build, Construction, ElectricalServices, Plumbing, HomeRepairService, CleaningServices, LocalLaundryService,
  AcUnit, Spa, ContentCut, SelfImprovement, FaceRetouchingNatural,
  // Additional common icons
  Category, Warning, Info, CheckCircle, Error, Help, Notifications, Search as SearchIcon, AccessTime,
  LocalAtm, AccountBalance, CreditCard, Payment, ShoppingCart, Store, Timeline, Brush, PhotoCamera, MusicNote,
  SportsSoccer, FitnessCenter, Pets, ChildCare, School, LocalLibrary, Agriculture, LocalFlorist, Event as EventIcon,
} from '@mui/icons-material';

export interface IconPickerProps {
  selectedIcon: string;
  onIconSelect: (iconName: string) => void;
  label?: string;
}

// Define available icons with their components and categories
const availableIcons = [
  // Action Icons
  { name: 'Add', component: Add, category: 'Action', keywords: ['plus', 'create', 'new'] },
  { name: 'Edit', component: Edit, category: 'Action', keywords: ['modify', 'change', 'update'] },
  { name: 'Delete', component: Delete, category: 'Action', keywords: ['remove', 'trash', 'delete'] },
  { name: 'Save', component: Save, category: 'Action', keywords: ['save', 'store', 'keep'] },
  { name: 'Close', component: Close, category: 'Action', keywords: ['close', 'exit', 'cancel'] },
  { name: 'Done', component: Done, category: 'Action', keywords: ['complete', 'finish', 'check'] },
  { name: 'Cancel', component: Cancel, category: 'Action', keywords: ['cancel', 'abort', 'stop'] },
  { name: 'Refresh', component: Refresh, category: 'Action', keywords: ['refresh', 'reload', 'update'] },
  { name: 'Settings', component: Settings, category: 'Action', keywords: ['settings', 'config', 'options'] },
  { name: 'Share', component: Share, category: 'Action', keywords: ['share', 'send', 'distribute'] },
  { name: 'Star', component: Star, category: 'Action', keywords: ['star', 'favorite', 'bookmark'] },
  
  // Communication Icons
  { name: 'Phone', component: Phone, category: 'Communication', keywords: ['phone', 'call', 'telephone'] },
  { name: 'Email', component: Email, category: 'Communication', keywords: ['email', 'mail', 'message'] },
  { name: 'Message', component: Message, category: 'Communication', keywords: ['message', 'chat', 'text'] },
  { name: 'Chat', component: Chat, category: 'Communication', keywords: ['chat', 'conversation', 'talk'] },
  { name: 'VideoCall', component: VideoCall, category: 'Communication', keywords: ['video', 'call', 'meeting'] },
  
  // Food & Drink Icons
  { name: 'Restaurant', component: Restaurant, category: 'Food & Drink', keywords: ['restaurant', 'food', 'dining', 'eat'] },
  { name: 'LocalCafe', component: LocalCafe, category: 'Food & Drink', keywords: ['cafe', 'coffee', 'drink'] },
  { name: 'LocalPizza', component: LocalPizza, category: 'Food & Drink', keywords: ['pizza', 'food', 'delivery'] },
  { name: 'Cake', component: Cake, category: 'Food & Drink', keywords: ['cake', 'dessert', 'sweet'] },
  { name: 'LocalGroceryStore', component: LocalGroceryStore, category: 'Food & Drink', keywords: ['grocery', 'shopping', 'store'] },
  { name: 'Kitchen', component: Kitchen, category: 'Food & Drink', keywords: ['kitchen', 'cooking', 'home'] },
  
  // Healthcare Icons
  { name: 'LocalHospital', component: LocalHospital, category: 'Healthcare', keywords: ['hospital', 'medical', 'health'] },
  { name: 'MedicalServices', component: MedicalServices, category: 'Healthcare', keywords: ['medical', 'health', 'doctor'] },
  { name: 'LocalPharmacy', component: LocalPharmacy, category: 'Healthcare', keywords: ['pharmacy', 'medicine', 'drugs'] },
  
  // Emergency Icons
  { name: 'LocalFireDepartment', component: LocalFireDepartment, category: 'Emergency', keywords: ['fire', 'emergency', 'urgent', 'danger'] },
  { name: 'Warning', component: Warning, category: 'Emergency', keywords: ['warning', 'alert', 'caution', 'emergency'] },
  { name: 'ReportProblem', component: ReportProblem, category: 'Emergency', keywords: ['report', 'problem', 'issue', 'emergency'] },
  { name: 'Error', component: Error, category: 'Emergency', keywords: ['error', 'alert', 'critical', 'emergency'] },
  { name: 'Announcement', component: Announcement, category: 'Emergency', keywords: ['announcement', 'alert', 'urgent', 'important'] },
  { name: 'PriorityHigh', component: PriorityHigh, category: 'Emergency', keywords: ['priority', 'high', 'urgent', 'important'] },
  { name: 'NewReleases', component: NewReleases, category: 'Emergency', keywords: ['urgent', 'important', 'priority', 'emergency'] },
  { name: 'NotificationImportant', component: NotificationImportant, category: 'Emergency', keywords: ['notification', 'important', 'urgent', 'alert'] },
  
  // Home & Living Icons
  { name: 'Home', component: Home, category: 'Home & Living', keywords: ['home', 'house', 'residence'] },
  { name: 'House', component: House, category: 'Home & Living', keywords: ['house', 'building', 'property'] },
  
  // Transportation & Delivery Icons
  { name: 'LocalShipping', component: Shipping, category: 'Transportation', keywords: ['shipping', 'delivery', 'transport', 'truck'] },
  { name: 'DirectionsCar', component: DirectionsCar, category: 'Transportation', keywords: ['car', 'vehicle', 'auto'] },
  { name: 'DirectionsBike', component: DirectionsBike, category: 'Transportation', keywords: ['bike', 'bicycle', 'cycle'] },
  { name: 'LocalTaxi', component: LocalTaxi, category: 'Transportation', keywords: ['taxi', 'cab', 'ride'] },
  { name: 'Train', component: Train, category: 'Transportation', keywords: ['train', 'railway', 'transport'] },
  { name: 'Flight', component: Flight, category: 'Transportation', keywords: ['flight', 'airplane', 'travel'] },
  { name: 'TwoWheeler', component: TwoWheeler, category: 'Transportation', keywords: ['motorcycle', 'bike', 'scooter'] },
  
  // Services Icons
  { name: 'Build', component: Build, category: 'Services', keywords: ['build', 'construction', 'repair', 'tools'] },
  { name: 'Construction', component: Construction, category: 'Services', keywords: ['construction', 'building', 'work'] },
  { name: 'ElectricalServices', component: ElectricalServices, category: 'Services', keywords: ['electrical', 'electric', 'power', 'electricity'] },
  { name: 'Plumbing', component: Plumbing, category: 'Services', keywords: ['plumbing', 'pipes', 'water'] },
  { name: 'HomeRepairService', component: HomeRepairService, category: 'Services', keywords: ['repair', 'maintenance', 'fix', 'emergency'] },
  { name: 'CleaningServices', component: CleaningServices, category: 'Services', keywords: ['cleaning', 'housekeeping', 'maid'] },
  { name: 'LocalLaundryService', component: LocalLaundryService, category: 'Services', keywords: ['laundry', 'washing', 'clothes'] },
  { name: 'AcUnit', component: AcUnit, category: 'Services', keywords: ['ac', 'air conditioning', 'cooling', 'hvac'] },
  { name: 'Spa', component: Spa, category: 'Services', keywords: ['spa', 'wellness', 'relaxation', 'massage'] },
  { name: 'ContentCut', component: ContentCut, category: 'Services', keywords: ['salon', 'haircut', 'beauty', 'grooming'] },
  { name: 'SelfImprovement', component: SelfImprovement, category: 'Services', keywords: ['beauty', 'wellness', 'self-care', 'spa'] },
  { name: 'Beauty', component: FaceRetouchingNatural, category: 'Services', keywords: ['beauty', 'salon', 'cosmetic', 'makeup'] },
  
  // Technology Icons
  { name: 'Computer', component: Computer, category: 'Technology', keywords: ['computer', 'pc', 'desktop'] },
  { name: 'Smartphone', component: Smartphone, category: 'Technology', keywords: ['smartphone', 'mobile', 'phone'] },
  { name: 'Camera', component: PhotoCamera, category: 'Technology', keywords: ['camera', 'photo', 'picture'] },
  
  // Business Icons
  { name: 'Work', component: Work, category: 'Business', keywords: ['work', 'job', 'office'] },
  { name: 'Business', component: Business, category: 'Business', keywords: ['business', 'company', 'corporate'] },
  { name: 'Payment', component: Payment, category: 'Business', keywords: ['payment', 'money', 'finance'] },
  { name: 'CreditCard', component: CreditCard, category: 'Business', keywords: ['credit', 'card', 'payment'] },
  { name: 'ShoppingCart', component: ShoppingCart, category: 'Business', keywords: ['shopping', 'cart', 'buy'] },
  
  // People Icons
  { name: 'Person', component: Person, category: 'People', keywords: ['person', 'user', 'profile'] },
  { name: 'Group', component: Group, category: 'People', keywords: ['group', 'team', 'people'] },
  { name: 'ChildCare', component: ChildCare, category: 'People', keywords: ['childcare', 'baby', 'kids'] },
  
  // Places & Activities Icons
  { name: 'School', component: School, category: 'Places', keywords: ['school', 'education', 'learning'] },
  { name: 'LocalLibrary', component: LocalLibrary, category: 'Places', keywords: ['library', 'books', 'reading'] },
  { name: 'FitnessCenter', component: FitnessCenter, category: 'Places', keywords: ['fitness', 'gym', 'exercise'] },
  { name: 'SportsSoccer', component: SportsSoccer, category: 'Places', keywords: ['sports', 'soccer', 'football'] },
  { name: 'Pets', component: Pets, category: 'Places', keywords: ['pets', 'animals', 'veterinary'] },
  
  // General Icons
  { name: 'Category', component: Category, category: 'General', keywords: ['category', 'general', 'default'] },
  { name: 'LocationOn', component: LocationOn, category: 'General', keywords: ['location', 'map', 'place'] },
  { name: 'AccessTime', component: AccessTime, category: 'General', keywords: ['time', 'clock', 'schedule'] },
  { name: 'Event', component: EventIcon, category: 'General', keywords: ['event', 'calendar', 'date'] },
];

const IconPicker: React.FC<IconPickerProps> = ({ 
  selectedIcon, 
  onIconSelect, 
  label = "Select Icon" 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Get unique categories
  const categories = useMemo(() => {
    const cats = Array.from(new Set(availableIcons.map(icon => icon.category)));
    return cats.sort();
  }, []);

  // Filter icons based on search term and category
  const filteredIcons = useMemo(() => {
    return availableIcons.filter(icon => {
      const matchesSearch = searchTerm === '' || 
        icon.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        icon.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        icon.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = selectedCategory === '' || icon.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const clearSearch = () => {
    setSearchTerm('');
    setSelectedCategory('');
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      
      {/* Search and Filter Controls */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search icons..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />
        
        {/* Category Filter Chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Chip
            label="All"
            size="small"
            variant={selectedCategory === '' ? 'filled' : 'outlined'}
            onClick={() => setSelectedCategory('')}
            color={selectedCategory === '' ? 'primary' : 'default'}
          />
          {categories.map((category) => (
            <Chip
              key={category}
              label={category}
              size="small"
              variant={selectedCategory === category ? 'filled' : 'outlined'}
              onClick={() => setSelectedCategory(category)}
              color={selectedCategory === category ? 'primary' : 'default'}
            />
          ))}
          {(searchTerm || selectedCategory) && (
            <Chip
              label="Clear"
              size="small"
              variant="outlined"
              color="secondary"
              onClick={clearSearch}
              deleteIcon={<Clear />}
              onDelete={clearSearch}
            />
          )}
        </Box>
      </Box>

      {/* Results Count */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {filteredIcons.length} matching results
      </Typography>
      
      {/* Icon Grid */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          bgcolor: 'background.paper',
          maxHeight: 400,
          overflowY: 'auto',
        }}
      >
        {filteredIcons.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No icons found matching your search
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={0.5}>
            {filteredIcons.map((icon) => {
              const IconComponent = icon.component;
              const isSelected = selectedIcon === icon.name;
              
              return (
                <Grid item xs={2} sm={1.5} md={1} key={icon.name}>
                  <Tooltip title={`${icon.name} (${icon.category})`} arrow placement="top">
                    <IconButton
                      onClick={() => onIconSelect(icon.name)}
                      sx={{
                        width: 40,
                        height: 40,
                        border: '2px solid',
                        borderColor: isSelected ? 'primary.main' : 'transparent',
                        bgcolor: isSelected ? 'primary.light' : 'transparent',
                        color: isSelected ? 'primary.dark' : 'text.primary',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.light' : 'action.hover',
                          borderColor: isSelected ? 'primary.main' : 'action.selected',
                        },
                        transition: 'all 0.2s ease-in-out',
                        '& .MuiSvgIcon-root': {
                          fontSize: 20,
                        },
                      }}
                    >
                      <IconComponent />
                    </IconButton>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
      
      {/* Selected Icon Display */}
      {selectedIcon && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Selected: 
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            {React.createElement(availableIcons.find(icon => icon.name === selectedIcon)?.component || Category)}
            <Typography variant="body2" fontWeight={500} sx={{ ml: 1 }}>
              {selectedIcon}
            </Typography>
            <Chip 
              label={availableIcons.find(icon => icon.name === selectedIcon)?.category || 'Other'} 
              size="small" 
              sx={{ ml: 1 }} 
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default IconPicker;
